/**
 * Packages and price sheets — `/v1/admin/packages*`, `/v1/admin/price-versions*`,
 * `/v1/admin/fee-rules*`, `/v1/admin/travel-zones/{id}/charge-rules`, `/v1/admin/travel-charge-rules*`.
 *
 * **Nothing in this module computes money.** Every amount is an integer number of paise the server
 * sent; the screen formats it for display and sends integers back. There is no client-side total,
 * no rounding, and no rupee arithmetic — a second implementation of the pricing maths would be a
 * second answer to what a devotee owes.
 *
 * **A published version is frozen, and the server is what says so.** Editing one comes back
 * `409 version_frozen`; the screen shows that refusal rather than pre-emptively disabling
 * everything, because "the button did nothing" is a worse explanation than the server's own.
 *
 * **A publish is a new version, never an edit.** The flow is clone-to-draft → edit → publish, and
 * the previous version is archived rather than overwritten, so a booking priced against it still
 * reproduces its invoice.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PackageRow = components['schemas']['PackageRow'];
type PackageCreateSchema = components['schemas']['PackageCreateIn'];
type PackageCreateRequired = 'tier' | 'name' | 'duration_minutes';
/** The generated type marks the server's defaulted fields required; the request may omit them. */
export type PackageCreateIn = Pick<PackageCreateSchema, PackageCreateRequired> &
  Partial<Omit<PackageCreateSchema, PackageCreateRequired>>;
export type PackageListEntry = components['schemas']['PackageListEntry'];
export type PriceVersionRow = components['schemas']['PriceVersionRow'];
export type PriceVersionDetail = components['schemas']['PriceVersionDetail'];
export type LineItemRow = components['schemas']['LineItemRow'];
export type LineItemIn = components['schemas']['LineItemIn'];
export type CityOverrideIn = components['schemas']['CityOverrideIn'];
export type ScopeOverrideIn = components['schemas']['ScopeOverrideIn'];
export type FeeRuleRow = components['schemas']['FeeRuleRow'];
export type ResolvedSheet = components['schemas']['ResolvedSheetResponse'];
type PackageList = components['schemas']['PackageListResponse'];
type VersionList = components['schemas']['PriceVersionListResponse'];
type FeeRuleList = components['schemas']['FeeRuleListResponse'];
export type FeeRuleCreateIn = components['schemas']['FeeRuleCreateIn'];
export type FeeRulePatchIn = components['schemas']['FeeRulePatchIn'];
export type TravelRuleRow = components['schemas']['TravelChargeRuleRow'];
type TravelRuleCreateSchema = components['schemas']['TravelChargeRuleCreateIn'];
/** As with packages: the server defaults the amounts, so the request may omit them. */
export type TravelRuleCreateIn = Pick<TravelRuleCreateSchema, 'active_from'> &
  Partial<Omit<TravelRuleCreateSchema, 'active_from'>>;
export type TravelRulePatchIn = components['schemas']['TravelChargeRulePatchIn'];
type TravelRuleList = components['schemas']['TravelChargeRuleListResponse'];
export type TravelSample = components['schemas']['TravelSampleResponse'];

/** A draft travel rule and a distance to price it at. */
export interface TravelSampleQuery extends Record<string, unknown> {
  distance_km: string;
  free_within_km: number;
  base_paise: number;
  per_km_paise: number;
  cap_paise: number | null;
}

export function usePackages(pujaId: number | undefined) {
  return useQuery({
    queryKey: keys.pricing.packages(pujaId ?? 0),
    queryFn: () => authedClient.request<PackageList>(`/v1/admin/pujas/${pujaId}/packages`),
    enabled: pujaId !== undefined,
  });
}

export function usePriceVersions(packageId: number | undefined) {
  return useQuery({
    queryKey: keys.pricing.versions(packageId ?? 0),
    queryFn: () => authedClient.request<VersionList>(`/v1/admin/packages/${packageId}/price-versions`),
    enabled: packageId !== undefined,
  });
}

export function usePriceVersion(versionId: number | undefined) {
  return useQuery({
    queryKey: keys.pricing.version(versionId ?? 0),
    queryFn: () => authedClient.request<PriceVersionDetail>(`/v1/admin/price-versions/${versionId}`),
    enabled: versionId !== undefined,
  });
}

/**
 * The dry run for one city. `cityId` undefined means "version defaults" — a distinct answer
 * from any city's, and the response says which one it gave via `city_resolved`.
 */
export function usePricePreview(versionId: number | undefined, cityId: number | undefined) {
  return useQuery({
    queryKey: keys.pricing.preview(versionId ?? 0, cityId),
    queryFn: () =>
      authedClient.request<ResolvedSheet>(
        `/v1/admin/price-versions/${versionId}/preview${cityId === undefined ? '' : `?city_id=${cityId}`}`,
      ),
    enabled: versionId !== undefined,
  });
}

export type QuotePreview = components['schemas']['PriceQuotePreview'];

/**
 * The sheet through the quote resolver for one city (doc 04-backend/17 §2): fee rules included,
 * travel shown as deferred when it needs an address. `drop` names optional line item types the
 * devotee would untick; the server still prices them and echoes them with `dropped: true`.
 */
export function useQuotePreview(versionId: number, cityId: number, drop: readonly string[]) {
  return useQuery({
    queryKey: keys.pricing.quote(versionId, cityId, drop),
    queryFn: () => {
      const q = new URLSearchParams({ city_id: String(cityId) });
      for (const d of drop) q.append('drop_optional', d);
      return authedClient.request<QuotePreview>(`/v1/admin/price-versions/${versionId}/quote-preview?${q}`);
    },
  });
}

export function useFeeRules() {
  return useQuery({
    queryKey: keys.pricing.feeRules(),
    queryFn: () => authedClient.request<FeeRuleList>('/v1/admin/fee-rules'),
  });
}

export function useTravelRules(zoneId: number | undefined) {
  return useQuery({
    queryKey: keys.pricing.travelRules(zoneId ?? 0),
    queryFn: () =>
      authedClient.request<TravelRuleList>(`/v1/admin/travel-zones/${zoneId}/charge-rules`),
    enabled: zoneId !== undefined,
  });
}

/**
 * The worked sample under the travel-rule form (admin doc 11 §3.6), priced by the quote engine's
 * own `resolve_travel_charge` against the draft amounts — never client maths. `undefined` waits
 * for a form that parses.
 */
export function useTravelSample(params: TravelSampleQuery | undefined) {
  const query = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) if (v !== null) query.set(k, String(v));
  }
  return useQuery({
    queryKey: keys.pricing.travelSample(params ?? {}),
    queryFn: () => authedClient.request<TravelSample>(`/v1/admin/travel-charge-rules/sample?${query}`),
    enabled: params !== undefined,
  });
}

/**
 * Every pricing write invalidates every pricing read.
 *
 * Coarse on purpose: publishing changes the version list, the sheet, the preview and which tier is
 * bookable, and a hand-maintained list of which keys each write touches is a list that goes stale
 * the first time somebody adds a field.
 *
 * `TData` is threaded through rather than swallowed — the draft-creation caller needs the new
 * version's id to select it, and `unknown` would push a cast into the screen.
 */
function usePricingMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pricing.all() }),
  });
}

export function useCreateDraft() {
  return usePricingMutation((packageId: number) =>
    authedClient.request<PriceVersionDetail>(`/v1/admin/packages/${packageId}/price-versions`, {
      method: 'POST',
    }),
  );
}

/** "Restore these prices": an archived sheet cloned into a new draft (doc 11 §2). The old
 *  sheet stays frozen; the draft goes live only through the same publish. */
export function useRestoreVersion() {
  return usePricingMutation(({ packageId, fromVersionId }: { packageId: number; fromVersionId: number }) =>
    authedClient.request<PriceVersionDetail>(`/v1/admin/packages/${packageId}/price-versions`, {
      method: 'POST',
      body: { from_version_id: fromVersionId },
    }),
  );
}

export function useReplaceLineItems() {
  return usePricingMutation(({ versionId, items }: { versionId: number; items: LineItemIn[] }) =>
    authedClient.request<PriceVersionDetail>(`/v1/admin/price-versions/${versionId}/line-items`, {
      method: 'PUT',
      body: { items },
    }),
  );
}

/**
 * A draft's per-city amounts, replaced whole.
 *
 * **Version-scoped, not city-scoped** — an override belongs to the price version it changes, so
 * it is edited beside the line items it overrides and the preview that shows their effect. A
 * city-first surface would have to enumerate every draft to find the ones touching that city,
 * and would put the write a screen away from the read that verifies it.
 */
export function useReplaceCityOverrides() {
  return usePricingMutation(
    ({ versionId, items }: { versionId: number; items: CityOverrideIn[] }) =>
      authedClient.request<PriceVersionDetail>(
        `/v1/admin/price-versions/${versionId}/city-overrides`,
        { method: 'PUT', body: { items } },
      ),
  );
}

/** A draft's per-language and per-sampradaya amounts, replaced whole (12-content/08 §1.2). */
export function useReplaceScopeOverrides() {
  return usePricingMutation(
    ({ versionId, items }: { versionId: number; items: ScopeOverrideIn[] }) =>
      authedClient.request<PriceVersionDetail>(
        `/v1/admin/price-versions/${versionId}/scope-overrides`,
        { method: 'PUT', body: { items } },
      ),
  );
}

export function usePublishVersion() {
  return usePricingMutation(
    ({ versionId, changeNote }: { versionId: number; changeNote: string }) =>
      authedClient.request<PriceVersionDetail>(`/v1/admin/price-versions/${versionId}/publish`, {
        method: 'POST',
        body: { change_note: changeNote },
      }),
  );
}

export function useDiscardDraft() {
  return usePricingMutation((versionId: number) =>
    authedClient.request<void>(`/v1/admin/price-versions/${versionId}`, { method: 'DELETE' }),
  );
}

/* ── Tiers ─────────────────────────────────────────────────────────────────────────────────
   Created, described and retired from the puja catalogue; priced on the pricing screen. Both
   read the same `pricing.packages` query, so a tier made in one is there in the other. */

export function useCreatePackage() {
  return usePricingMutation(({ pujaId, body }: { pujaId: number; body: PackageCreateIn }) =>
    authedClient.request<PackageRow>(`/v1/admin/pujas/${pujaId}/packages`, {
      method: 'POST',
      body,
    }),
  );
}

export function useSetPackageActive() {
  return usePricingMutation(({ packageId, active }: { packageId: number; active: boolean }) =>
    authedClient.request<PackageRow>(`/v1/admin/packages/${packageId}`, {
      method: 'PATCH',
      body: { is_active: active },
    }),
  );
}

/** One list replaced whole; the other is left out of the body, so the server keeps it. */
export function useReplacePackageList() {
  return usePricingMutation(
    ({
      packageId,
      list,
      items,
    }: {
      packageId: number;
      list: 'inclusions' | 'exclusions';
      items: PackageListEntry[];
    }) =>
      authedClient.request<PackageRow>(`/v1/admin/packages/${packageId}/lists`, {
        method: 'PUT',
        body: { [list]: items },
      }),
  );
}

export function useRetirePackage() {
  return usePricingMutation((packageId: number) =>
    authedClient.request<void>(`/v1/admin/packages/${packageId}`, { method: 'DELETE' }),
  );
}

/* ── Fee and travel rules ──────────────────────────────────────────────────────────────────
   A patch carries only the fields that changed. A rule in use refuses any amount field with
   `409 rule_in_use` even when its value is unchanged, so resending the whole row would turn
   "switch it off" into a refusal. */

export function useCreateFeeRule() {
  return usePricingMutation((body: FeeRuleCreateIn) =>
    authedClient.request<FeeRuleRow>('/v1/admin/fee-rules', { method: 'POST', body }),
  );
}

export function usePatchFeeRule() {
  return usePricingMutation(({ ruleId, patch }: { ruleId: number; patch: FeeRulePatchIn }) =>
    authedClient.request<FeeRuleRow>(`/v1/admin/fee-rules/${ruleId}`, { method: 'PATCH', body: patch }),
  );
}

export function useCreateTravelRule() {
  return usePricingMutation(({ zoneId, body }: { zoneId: number; body: TravelRuleCreateIn }) =>
    authedClient.request<TravelRuleRow>(`/v1/admin/travel-zones/${zoneId}/charge-rules`, {
      method: 'POST',
      body,
    }),
  );
}

export function usePatchTravelRule() {
  return usePricingMutation(({ ruleId, patch }: { ruleId: number; patch: TravelRulePatchIn }) =>
    authedClient.request<TravelRuleRow>(`/v1/admin/travel-charge-rules/${ruleId}`, {
      method: 'PATCH',
      body: patch,
    }),
  );
}
